import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, X, Camera, Clock, CurrencyInr, Star, MapPin, Briefcase, Calendar, Copy, ShareNetwork, Wallet, ArrowDown, ArrowUp, CheckCircle, XCircle, HourglassSimple, LinkSimple, UserPlus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import InviteAndEarn from '../components/InviteAndEarn';
import ProAttendanceWidget from '../components/ProAttendanceWidget';
import BrandLogo from '../components/BrandLogo';
import DeleteAccountSection from '../components/DeleteAccountSection';
import Footer from '../components/Footer';
import WaiterOffersCard from '../components/WaiterOffersCard';
import IdBadge from '../components/IdBadge';
import PlatformCommissionBanner from '../components/PlatformCommissionBanner';
import DecorationTeamSelector from '../components/DecorationTeamSelector';
import ProDualPriceSelector from '../components/ProDualPriceSelector';
import CateringTeamPricingMatrix from '../components/CateringTeamPricingMatrix';
import KycCard from '../components/KycCard';
import BookingsBoard from '../components/BookingsBoard';
import JoiningFeeCard from '../components/JoiningFeeCard';
import ServingCitiesCard from '../components/ServingCitiesCard';
import ProPendingOffersCard from '../components/ProPendingOffersCard';
import ProDecorPackagesCard from '../components/ProDecorPackagesCard';
import AdminFilledContentBanner from '../components/AdminFilledContentBanner';
import ProPortfolioCard from '../components/ProPortfolioCard';
import MetaSocialConnectCard from '../components/MetaSocialConnectCard';
import CustomerInquiriesPanel from '../components/CustomerInquiriesPanel';
import BuyLeadsCard from '../components/BuyLeadsCard';
import CalendarAvailability from '../components/CalendarAvailability';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROFESSIONAL_LABELS = {
  chef: 'Chef',
  biryani_specialist: 'Biryani Specialist',
  assistant_chef: 'Assistant Chef',
  server_waiter: 'Server / Waiter',
  priest: 'Priest / Purohit',
  photographer: 'Photographer',
  videographer: 'Videographer',
  magician: 'Magician',
  makeup_artist: 'Makeup Artist',
  mehndi_artist: 'Mehndi Artist',
  choreographer: 'Choreographer',
  decoration_team: 'Decoration Team',
  singer: 'Singer / Vocalist',
  singer_karaoke: 'Singer with Karaoke',
  musician: 'Musician / Instrument Player',
  music_instrument_performer: 'Music Instrument Performer',
  nadaswaram_artist: 'Nadaswaram Artist',
  thavil_artist: 'Thavil Artist',
  dancer: 'Dancer / Dance Troupe',
  anchor_emcee: 'Anchor / Emcee',
  catering_team: 'Catering Team',
  return_gift: 'Return Gift / Muhurtham Pai',
  // New entertainment categories
  standup_comedian: 'Standup Comedian',
  costume_performer: 'Costume Performer',
  buffoon: 'Buffoon / Clown',
  mimicry_artist: 'Mimicry Artist',
  dj: 'DJ',
};


const ProfessionalDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    price_per_event: '',
    price_per_hour: '',
    experience_years: '',
    specialization: '',
    description: '',
    location: '',
  });

  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    base_price: '',
    price_per_hour: '',
    photos: [],
  });

  useEffect(() => {
    if (user) {
      fetchData();
      setProfileData({
        price_per_event: user.price_per_event || '',
        price_per_hour: user.price_per_hour || '',
        experience_years: user.experience_years || '',
        specialization: user.specialization || '',
        description: user.description || '',
        location: user.location || '',
      });
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [svcRes, bookRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/services/vendor/${user.id}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/bookings/vendor`, { withCredentials: true }),
      ]);
      setServices(svcRes.data);
      setBookings(bookRes.data);
    } catch {}
  };

  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/professionals/profile`, profileData, { withCredentials: true });
      toast.success('Profile updated!');
      setShowProfileEdit(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (serviceForm.photos.length < 3) {
        setServiceForm({ ...serviceForm, photos: [...serviceForm.photos, resp.data.url] });
        toast.success('Photo uploaded!');
      }
    } catch {
      toast.error('Upload failed');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setServiceForm({ ...serviceForm, photos: serviceForm.photos.filter((_, i) => i !== idx) });
  };

  // Decoration Team has a dedicated minimal Quick Add modal — bypass the
  // generic service form entirely. Payload shape lives at
  // `services.packages[0].decoration_team_data`.
  const handleSaveDecorationTeam = async ({ photos, event_prices }) => {
    try {
      const payload = {
        name: 'Decoration Team',
        category: 'decoration_team',
        description: 'Decoration Team services',
        base_price: parseFloat(event_prices.marriage || event_prices.other_events || 0),
        event_type: ['wedding', 'birthday', 'other'],
        packages: [{
          photos,
          decoration_team_data: { photos, event_prices },
        }],
      };
      const existing = services[0];
      if (existing) {
        await axios.put(`${BACKEND_URL}/api/services/${existing.id}`, payload, { withCredentials: true });
        toast.success('Decoration Team updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/services`, payload, { withCredentials: true });
        toast.success('Decoration Team saved!');
      }
      fetchData();
      setShowServiceForm(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const resetServiceForm = () => {
    setServiceForm({ name: '', description: '', base_price: '', price_per_hour: '', photos: [] });
    setEditingService(null);
  };

  const handleEditService = (svc) => {
    setEditingService(svc);
    const pkg = svc.packages?.[0] || {};
    setServiceForm({
      name: svc.name || '',
      description: svc.description || '',
      base_price: svc.base_price || '',
      price_per_hour: pkg.price_per_hour || '',
      photos: pkg.photos || [],
    });
    setShowServiceForm(true);
  };

  const handleCreateOrUpdateService = async (e) => {
    e.preventDefault();

    const autoName = serviceForm.name || `${PROFESSIONAL_LABELS[user.category] || user.category} Service`;

    const payload = {
      name: autoName,
      category: user.category,
      description: serviceForm.description || `Professional ${PROFESSIONAL_LABELS[user.category] || user.category} services`,
      base_price: parseFloat(serviceForm.base_price) || 0,
      event_type: ['wedding', 'birthday'],
      packages: [{
        price_per_event: serviceForm.base_price,
        price_per_hour: serviceForm.price_per_hour,
        photos: serviceForm.photos,
      }],
    };

    try {
      if (editingService) {
        await axios.put(`${BACKEND_URL}/api/services/${editingService.id}`, payload, { withCredentials: true });
        toast.success('Service updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/services`, payload, { withCredentials: true });
        toast.success('Service created!');
      }
      fetchData();
      setShowServiceForm(false);
      resetServiceForm();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDeleteService = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/services/${id}`, { withCredentials: true });
      toast.success('Service deleted!');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/bookings/${bookingId}/status?status=${status}`, {}, { withCredentials: true });
      toast.success('Booking status updated!');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-sand-50">
      {/* Header */}
      <div className="border-b border-[#E8E1D5] bg-white">
        <div className="max-w-6xl mx-auto px-8 py-4 flex items-center justify-between">
          <BrandLogo size="md" linkTo="/" />
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <span className="text-sm text-text-secondary">{user.name}</span>
            <Button onClick={logout} variant="outline" size="sm" className="rounded-none border-[#E8E1D5]" data-testid="logout-btn">Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-8 py-8 flex flex-col">
        <AdminFilledContentBanner />
        {/* Platform commission visibility — sits AFTER the active business
            sections (matches vendor dashboard order-11). */}
        <div className="mb-6 order-11">
          <PlatformCommissionBanner role="professional" category={user?.category} />
        </div>

        {/* Profile Card */}
        <Card className="mb-6 bg-white border-[#E8E1D5] order-1" data-testid="professional-profile-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="font-heading text-3xl">{user.name}</CardTitle>
                <div className="flex items-center gap-3 mt-1 text-text-secondary text-sm flex-wrap">
                  <span className="bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded font-bold text-xs uppercase tracking-wider">
                    {PROFESSIONAL_LABELS[user.category] || user.category}
                  </span>
                  {user.location && <span className="flex items-center gap-1"><MapPin size={14} /> {user.location}</span>}
                  {user.display_id && <IdBadge id={user.display_id} label="Pro ID" testId="professional-id-badge" />}
                </div>
              </div>
              <Button onClick={() => setShowProfileEdit(!showProfileEdit)} variant="outline" size="sm" className="rounded-none border-[#E8E1D5]" data-testid="edit-profile-btn">
                <Pencil size={16} className="mr-1" /> Edit Profile
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {user.category !== 'catering_team' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="border border-[#E8E1D5] rounded p-3 text-center">
                  <CurrencyInr size={20} className="mx-auto mb-1 text-brand-primary" />
                  <div className="text-xs text-text-secondary">Per Event</div>
                  <div className="font-heading text-lg font-bold">
                    {user.price_per_event ? `₹${Number(user.price_per_event).toLocaleString('en-IN')}` : 'Not set'}
                  </div>
                </div>
                <div className="border border-[#E8E1D5] rounded p-3 text-center">
                  <Clock size={20} className="mx-auto mb-1 text-brand-primary" />
                  <div className="text-xs text-text-secondary">Per Hour</div>
                  <div className="font-heading text-lg font-bold">
                    {user.price_per_hour ? `₹${Number(user.price_per_hour).toLocaleString('en-IN')}` : 'Not set'}
                  </div>
                </div>
                <div className="border border-[#E8E1D5] rounded p-3 text-center">
                  <Briefcase size={20} className="mx-auto mb-1 text-brand-primary" />
                  <div className="text-xs text-text-secondary">Experience</div>
                  <div className="font-heading text-lg font-bold">
                    {user.experience_years ? `${user.experience_years} yrs` : 'Not set'}
                  </div>
                </div>
                <div className="border border-[#E8E1D5] rounded p-3 text-center">
                  <Star size={20} className="mx-auto mb-1 text-brand-primary" />
                  <div className="text-xs text-text-secondary">Specialization</div>
                  <div className="font-heading text-sm font-bold truncate">
                    {user.specialization || 'Not set'}
                  </div>
                </div>
              </div>
            )}

            {/* Profile Edit Form */}
            {showProfileEdit && (
              <div className="mt-4 border-t border-[#E8E1D5] pt-4 space-y-4">
                {/* Catering Team uses the Pricing Matrix below — these
                    generic per-event / per-hour / specialization fields
                    are irrelevant and confusing for that role. */}
                {user.category !== 'catering_team' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Price per Event (₹)</Label>
                      <Input type="number" value={profileData.price_per_event}
                        onChange={(e) => setProfileData({ ...profileData, price_per_event: e.target.value })}
                        placeholder="e.g. 15000" className="rounded-none border-[#E8E1D5] h-9" data-testid="profile-price-event" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Price per Hour (₹)</Label>
                      <Input type="number" value={profileData.price_per_hour}
                        onChange={(e) => setProfileData({ ...profileData, price_per_hour: e.target.value })}
                        placeholder="e.g. 2000" className="rounded-none border-[#E8E1D5] h-9" data-testid="profile-price-hour" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Experience (Years)</Label>
                      <Input type="number" value={profileData.experience_years}
                        onChange={(e) => setProfileData({ ...profileData, experience_years: e.target.value })}
                        placeholder="e.g. 5" className="rounded-none border-[#E8E1D5] h-9" data-testid="profile-experience" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-text-secondary">Specialization</Label>
                      <Input type="text" value={profileData.specialization}
                        onChange={(e) => setProfileData({ ...profileData, specialization: e.target.value })}
                        placeholder="e.g. South Indian Cuisine, Wedding specialist..." className="rounded-none border-[#E8E1D5] h-9" data-testid="profile-specialization" />
                    </div>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-text-secondary">Location</Label>
                  <Input type="text" value={profileData.location}
                    onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                    placeholder="e.g. Chennai" className="rounded-none border-[#E8E1D5] h-9" data-testid="profile-location" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-text-secondary">About You</Label>
                  <Textarea value={profileData.description}
                    onChange={(e) => setProfileData({ ...profileData, description: e.target.value })}
                    rows={3} placeholder="Describe your services..." className="rounded-none border-[#E8E1D5]" data-testid="profile-description" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleUpdateProfile} className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-profile-btn">
                    Save Profile
                  </Button>
                  <Button onClick={() => setShowProfileEdit(false)} variant="outline" className="rounded-none border-[#E8E1D5]">Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* KYC — moved to the bottom (Feb 2026) per user feedback */}
        <div className="mb-8 order-90">
          <KycCard />
        </div>

        {/* Joining fee — Phase B revenue gate (KYC approved + fee paid → active). */}
        <div className="mb-8 order-91">
          <JoiningFeeCard />
        </div>

        {/* Serving cities (Feb 2026) — INDEPENDENT of KYC. Sits right below Pricing
            so pros immediately declare where they work. */}
        <div className="mb-8 order-4">
          <ServingCitiesCard />
        </div>

        {/* Pending booking offers — accept/decline customer requests.
            This is the Pro equivalent of vendor's "Booking Offers Inbox" (order-10). */}
        <div className="mb-8 order-10">
          <ProPendingOffersCard />
        </div>

        {/* Phase 3: Unified dual-pricing & portfolio card for eligible pros.
            Shown to every professional category EXCEPT `server_waiter`,
            `catering_team`, and `return_gift` (they keep their per-person /
            per-piece flows handled elsewhere). Decoration Team continues to
            use its dedicated selector for backwards-compat. */}
        {!['server_waiter', 'catering_team', 'return_gift', 'decoration_team'].includes(user.category) && (
          <div className="mb-8 order-2">
            <h2 className="font-heading text-2xl mb-4">Pricing &amp; Portfolio</h2>
            <ProDualPriceSelector categoryLabel={PROFESSIONAL_LABELS[user.category] || user.category} />
          </div>
        )}

        {/* Decoration Team — simple decor packages (image + price + wedding/other tag).
            Up to 10 packages per pro; surfaces under their card in Instant Pricing. */}
        {user.category === 'decoration_team' && (
          <div className="mb-8 order-2">
            <h2 className="font-heading text-2xl mb-4">Decor Packages</h2>
            <ProDecorPackagesCard />
          </div>
        )}

        {/* Catering Team — 2-axis pricing matrix (guests × menu items).
            All-inclusive prices (cooking, serving, cleanup, intra-city
            transport). Shown ONLY for category=catering_team. */}
        {user.category === 'catering_team' && (
          <div className="mb-8 order-2">
            <h2 className="font-heading text-2xl mb-4">Pricing Matrix</h2>
            <CateringTeamPricingMatrix />
          </div>
        )}

        {/* Pro Portfolio Gallery — 10 images + 3 videos, no-caption rule, AI moderated.
            Shown for every professional category except server/waiter (they're hired
            by headcount, not by visual showreel). Sits at order-[40] — visible
            but below the money-making sections, above KYC/joining-fee. */}
        {!['server_waiter', 'waiter', 'server'].includes(user.category) && (
          <div className="mb-8 order-[40]">
            <h2 className="font-heading text-2xl mb-4">Portfolio Gallery</h2>
            <ProPortfolioCard />
          </div>
        )}

        {/* Meta (Instagram + Facebook) connect — import photos + auto-post bookings */}
        {!['server_waiter', 'waiter', 'server'].includes(user.category) && (
          <div className="mb-8 order-[41]">
            <MetaSocialConnectCard />
          </div>
        )}

        {/* Customer Inquiries — every saved-package cart line that names this pro.
            Shows customer phone + Mark Booked button. */}
        <div className="mb-8 order-[5]">
          <CustomerInquiriesPanel />
        </div>

        {/* Buy Leads + Monthly Lift Pass */}
        <div className="mb-8 order-[6]">
          <BuyLeadsCard />
        </div>

        {/* Services Section — order-6 keeps it below pricing/cities */}
        <div className="mb-8 order-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl">Your Services</h2>
            <Button onClick={() => { resetServiceForm(); setShowServiceForm(true); }}
              className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="add-service-btn">
              <Plus size={18} className="mr-2" /> Add Service
            </Button>
          </div>

          {/* Service Form */}
          {showServiceForm && user.category === 'decoration_team' ? (
            <DecorationTeamSelector
              existing={services[0]?.packages?.[0]?.decoration_team_data}
              onSave={handleSaveDecorationTeam}
              onCancel={() => { setShowServiceForm(false); resetServiceForm(); }}
            />
          ) : showServiceForm && (
            <Card className="mb-6 bg-white border-[#E8E1D5]">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="font-heading text-2xl">{editingService ? 'Edit Service' : 'Create Service'}</CardTitle>
                  <Button onClick={() => { setShowServiceForm(false); resetServiceForm(); }} variant="ghost" size="sm"><X size={20} /></Button>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateOrUpdateService} className="space-y-4">
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoUpload} className="hidden" />

                  <div className="space-y-1">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Service Name</Label>
                    <Input value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                      placeholder={`e.g. ${PROFESSIONAL_LABELS[user.category] || ''} for Weddings`}
                      className="rounded-none border-[#E8E1D5]" data-testid="service-name-input" />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Description</Label>
                    <Textarea value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                      rows={2} placeholder="Describe this service..." className="rounded-none border-[#E8E1D5]" data-testid="service-desc-input" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Price per Event (₹)</Label>
                      <Input type="number" value={serviceForm.base_price} onChange={(e) => setServiceForm({ ...serviceForm, base_price: e.target.value })}
                        placeholder="e.g. 15000" className="rounded-none border-[#E8E1D5]" data-testid="service-price-event" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Price per Hour (₹)</Label>
                      <Input type="number" value={serviceForm.price_per_hour} onChange={(e) => setServiceForm({ ...serviceForm, price_per_hour: e.target.value })}
                        placeholder="e.g. 2000" className="rounded-none border-[#E8E1D5]" data-testid="service-price-hour" />
                    </div>
                  </div>

                  {/* Photos (up to 3) */}
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Photos (up to 3)</Label>
                    <div className="flex flex-wrap gap-2">
                      {serviceForm.photos.map((photo, pIdx) => (
                        <div key={pIdx} className="relative group">
                          <div className="w-20 h-20 border border-[#E8E1D5] rounded overflow-hidden">
                            <img src={`${BACKEND_URL}${photo}`} alt="" className="w-full h-full object-cover" />
                          </div>
                          <button type="button" onClick={() => removePhoto(pIdx)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <X size={10} weight="bold" />
                          </button>
                        </div>
                      ))}
                      {serviceForm.photos.length < 3 && (
                        <div className="w-20 h-20 border-2 border-dashed border-brand-primary/30 rounded flex flex-col items-center justify-center cursor-pointer hover:border-brand-primary transition-all"
                          onClick={() => fileInputRef.current?.click()} data-testid="add-service-photo">
                          <Camera size={20} className="text-brand-primary mb-1" />
                          <span className="text-[10px] text-text-secondary">Add</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button type="submit" className="flex-1 bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-service-btn">
                      {editingService ? 'Update Service' : 'Create Service'}
                    </Button>
                    <Button type="button" onClick={() => { setShowServiceForm(false); resetServiceForm(); }}
                      variant="outline" className="rounded-none border-[#E8E1D5]">Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Service Cards */}
          {services.length === 0 ? (
            <Card className="bg-white border-[#E8E1D5]">
              <CardContent className="py-8 text-center text-text-secondary">
                No services yet. Click "Add Service" to get started.
              </CardContent>
            </Card>
          ) : (
            services.map(svc => (
              <Card key={svc.id} className="mb-4 bg-white border-[#E8E1D5]" data-testid={`service-card-${svc.id}`}>
                <CardContent className="py-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-heading text-xl font-medium">{svc.name}</h3>
                      <p className="text-sm text-text-secondary">{svc.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEditService(svc)} variant="outline" size="sm" className="rounded-none border-[#E8E1D5]" data-testid={`edit-svc-${svc.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button onClick={() => handleDeleteService(svc.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-500 hover:bg-red-50" data-testid={`delete-svc-${svc.id}`}>
                        <X size={14} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {svc.base_price > 0 && <span className="text-brand-primary font-bold">₹{Number(svc.base_price).toLocaleString('en-IN')}/event</span>}
                    {svc.packages?.[0]?.price_per_hour && <span className="text-brand-primary font-bold">₹{Number(svc.packages[0].price_per_hour).toLocaleString('en-IN')}/hr</span>}
                  </div>
                  {svc.packages?.[0]?.photos?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {svc.packages[0].photos.map((p, pIdx) => (
                        <img key={pIdx} src={`${BACKEND_URL}${p}`} alt="" className="w-16 h-16 object-cover rounded border border-[#E8E1D5]" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Bookings Section */}
        <div>
          <h2 className="font-heading text-2xl mb-4">Bookings</h2>
          {bookings.length === 0 ? (
            <Card className="bg-white border-[#E8E1D5]">
              <CardContent className="py-8 text-center text-text-secondary">
                No bookings yet.
              </CardContent>
            </Card>
          ) : (
            bookings.map(booking => (
              <Card key={booking.id} className="mb-4 bg-white border-[#E8E1D5]" data-testid={`booking-${booking.id}`}>
                <CardContent className="py-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{booking.service_name}</h3>
                      <p className="text-sm text-text-secondary">Customer: {booking.customer_name}</p>
                      <p className="text-sm text-text-secondary">Event: {booking.event_date}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded font-bold uppercase ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{booking.status}</span>
                      {booking.status === 'pending' && (
                        <div className="flex gap-1">
                          <Button onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')} size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-none text-xs">Accept</Button>
                          <Button onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')} size="sm" variant="outline" className="rounded-none border-red-300 text-red-500 text-xs">Decline</Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Pending waiter offers — only renders for Server / Waiter category */}
        <div className="mb-8">
          <WaiterOffersCard />
        </div>

        {/* Invite & Earn Section */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl mb-4 flex items-center gap-2"><UserPlus size={24} className="text-brand-primary" /> {t('pro.invite_earn')}</h2>
          <ProAttendanceWidget />
          <InviteAndEarn />
        </div>

        {/* Calendar Availability */}
        <CalendarAvailability testIdPrefix="pro-cal" />

        {/* Order lifecycle inbox — Accept/Reject/Complete + customer phone */}
        <div className="mt-6">
          <BookingsBoard viewerRole="professional" title="Booking Requests" testId="pro-bookings-board" />
        </div>

        <DeleteAccountSection />
      </div>

      <Footer />
    </div>
  );
};

export default ProfessionalDashboard;
